// Image loading for export (CORS / proxy / cache)

// ─── image cache ──────────────────────────────────────────────────────────────

const imageCache = new Map<string, HTMLImageElement>();

/** 仅对跨域 http(s) URL 需要 CORS；data/blob 或同源无需设置，避免部分环境（如部分 WebP）异常 */
function isCrossOriginHttp(url: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (url.startsWith('data:') || url.startsWith('blob:')) return false;
    const u = new URL(url, window.location.href);
    return u.origin !== window.location.origin && (u.protocol === 'http:' || u.protocol === 'https:');
  } catch {
    return false;
  }
}

/**
 * 加载图片供 Canvas 绘制。导出时需未污染画布才能 toDataURL，因此：
 * - 跨域 http(s)：使用 crossOrigin='anonymous'，要求服务端返回 CORS 头；
 * - 若 Image 加载失败（常见于 WebP/跨域），尝试用 fetch 拉取后转 blob URL 再绘制（同源 blob 不污染）；
 * - 若仍失败且提供了 getImageProxyUrl，则通过后端代理拉取（如 WebP 同存储桶但无 CORS 时）。
 */
export function loadImage(src: string, getImageProxyUrl?: (s: string) => Promise<string | null>): Promise<HTMLImageElement> {
  if (imageCache.has(src)) return Promise.resolve(imageCache.get(src)!);

  return new Promise((resolve, reject) => {
    const img = new Image();
    if (isCrossOriginHttp(src)) img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageCache.set(src, img);
      resolve(img);
    };
    img.onerror = async () => {
      if (src.startsWith('data:') || src.startsWith('blob:')) {
        reject(new Error('Image load failed'));
        return;
      }
      try {
        const loaded = await loadImageViaFetch(src);
        resolve(loaded);
      } catch {
        if (getImageProxyUrl) {
          try {
            const proxyUrl = await getImageProxyUrl(src);
            if (proxyUrl) {
              const proxyImg = await loadImage(proxyUrl, undefined);
              imageCache.set(src, proxyImg);
              resolve(proxyImg);
              return;
            }
          } catch {
            // ignore
          }
        }
        reject(new Error('Image load failed'));
      }
    };
    img.src = src;
  });
}

/** Image 加载失败时用 fetch 拉取（仍受 CORS 限制），成功则用 blob URL 绘制，不污染画布 */
export function loadImageViaFetch(src: string): Promise<HTMLImageElement> {
  return fetch(src, { mode: 'cors' })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.blob();
    })
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          imageCache.set(src, img);
          resolve(img);
        };
        img.onerror = () => {
          URL.revokeObjectURL(blobUrl);
          reject(new Error('Blob image load failed'));
        };
        img.src = blobUrl;
      });
    });
}
